import { getAssetPath } from '@/constants/configs';
import {
  defaultConfigs,
  getExtraBasicInfoQuestions as defaultGetExtraBasicInfoQuestions,
  resolveICBWebUrl,
} from '@/constants/tenant-configs/default';

import { AccountType, AccountTypeStatus } from '@/types/api/account';
import { QuestionCode } from '@/types/api/pep';
import {
  AppConfigurations,
  ExtraBasicInfoQuestions,
} from '@/types/app-configurations';
import { DocumentType } from '@/types/document';
import { Profession, User } from '@/types/user';

import {
  isBusinessPurposeQuestionEnabled,
  isExistingCustomer,
  isMinorUser,
  isRightStartUser,
} from '@/utils/user';

export const barbadosConfigs: AppConfigurations = {
  ...defaultConfigs,
  links: {
    ...defaultConfigs.links,
    privacyPolicyURL: 'https://republicbarbados.com/about/privacy-policy',
    termsAndConditionsURL:
      'https://www.republicbarbados.com/about/legal-notices',
    termsOfUseURL: getAssetPath('/tnc/republiconboard-tnc-brb.pdf'),
    appTermsAndConditionsURL: getAssetPath('/tnc/republiconboard-tnc-brb.pdf'),
    biometricConsentURL: getAssetPath(
      '/bio-consent/republiconboard-biometric-consent-brb.pdf',
    ),
    icbWeb: resolveICBWebUrl(
      'https://republiconlinebb.rfhl.com/RBBB.UI/#/administrationGeneral/login',
    ),
    icbIOS: 'https://apps.apple.com/us/app/republicmobile-bb/id6467048747',
    icbAndroid:
      'https://play.google.com/store/apps/details?id=bbic.com.republicprod',
    timeSaverURL: 'https://www.republicbarbados.com/personal/timesaver-account',
    eFreeURL: 'https://www.republicbarbados.com/personal/e-Free',
  },
  defaultCountryCode: '1246',
  locale: 'en-BB',
  isoCode: 'BRB',
  dateFormat: 'dd/MM/yyyy',
  defaultCurrency: 'BBD',
  excludedBranchCodes: ['14'],
  idCaptureRequirement: 'idCardOrPassport',
  idDocumentTypes: [
    DocumentType.Id,
    DocumentType.Passport,
    DocumentType.IdentityDriverLicense,
  ],
  shouldShowGDPRDrawer: true,
  showRiskSectorFlagQuestion: false,
  showSelfEmployedWithRiskSectorFlagQuestion: true,
  shouldRejectNonResidentMinor: true,
  existingCustomer: {
    maxAccountsPerType: 2,
  },
  autoApproveUnemployedProfessions: [
    Profession.Pensioner,
    Profession.Housewife,
    Profession.Unemployed,
  ],
  jointAccountRelationshipOptions: [
    { value: 'PARENT', i18nKey: 'minorOthers.form.relationship.parent' },
    {
      value: 'LEGAL_GUARDIAN',
      i18nKey: 'minorOthers.form.relationship.legalGuardian',
    },
  ],
};

export const getExtraBasicInfoQuestions = (
  user: User,
): ExtraBasicInfoQuestions => {
  if (isMinorUser(user)) {
    return defaultGetExtraBasicInfoQuestions(user);
  }

  return {
    ...defaultGetExtraBasicInfoQuestions(user),
    [QuestionCode.AccountPurpose]: {
      enabled: isBusinessPurposeQuestionEnabled(user),
      question: 'questionDrawer.accountPurpose.question',
      priority: 0,
    },
    [QuestionCode.EXCPrimaryAccount]: {
      enabled:
        isExistingCustomer(user) &&
        !isRightStartUser(user) &&
        !user.chargeAccountNo &&
        !!user.accountTypes?.find(
          type =>
            type.status === AccountTypeStatus.Active &&
            type.type === AccountType.Checking,
        ),
      question: 'questionDrawer.EXCPrimaryAccount.question',
      description: 'questionDrawer.EXCPrimaryAccount.description',
      priority: 8,
    },
  };
};
